import type { AlertEvent } from '@apm/contracts';
import { routeHref } from '../router';
import { serviceLink } from '../services/service-filter';
import { formatSpan } from '../time';
import { Chip, EmptyState } from '../ui';
import {
  alertLinks,
  alertMessage,
  alertSpanMs,
  formatAlertValue,
  SEVERITY_CARD,
  SEVERITY_LABEL,
  SEVERITY_TONE,
  signalLabel,
  suppressedNote,
} from './format';

/**
 * 지금 경보 중인 알림 카드 (10회차 결정 5).
 *
 * 실시간으로 즉시 갱신된다. 카드 하나가 규칙×대상 하나이고, 오래된 것이 위에 온다.
 */
export function ActiveAlerts({ alerts, now }: { alerts: readonly AlertEvent[]; now: number }) {
  if (alerts.length === 0) {
    return (
      <EmptyState
        title="경보 중인 알림이 없다"
        hint={['규칙 조건에 걸린 값이 없다.', '아래 규칙 목록에서 무엇을 감시하는지 볼 수 있다']}
      />
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {alerts.map((alert) => (
        <article
          key={alert.id}
          className={`rounded-xl border p-4 ${SEVERITY_CARD[alert.severity]}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <span className="flex flex-wrap items-center gap-1.5">
                <Chip tone={SEVERITY_TONE[alert.severity]}>{SEVERITY_LABEL[alert.severity]}</Chip>
                {suppressedNote(alert) === undefined ? null : (
                  <Chip tone="neutral" title="쿨다운·분당 상한에 걸려 채팅·로그로는 보내지 않았다">
                    {suppressedNote(alert)}
                  </Chip>
                )}
              </span>
              <h3 className="mt-2 break-words text-base font-semibold text-ink">
                {alert.ruleName}
              </h3>
              <p className="break-all text-xs text-muted">
                {signalLabel(alert)} · {alert.key}
              </p>
              <p className="mt-1 break-all text-xs text-muted">
                서비스 {alert.service ?? '전체 공통'} · 인스턴스 {alert.instance ?? '-'}
              </p>
            </div>
            <div className="shrink-0 text-right">
              <div className="tabular font-mono text-lg text-ink">{formatAlertValue(alert)}</div>
              <div className="tabular text-xs text-muted">
                {formatSpan(alertSpanMs(alert, now))} 지속
              </div>
            </div>
          </div>

          <p className="mt-3 break-all text-xs text-muted">{alertMessage(alert)}</p>

          <div className="mt-3 flex flex-wrap gap-2">
            {alertLinks(alert).map((link) => (
              <a
                key={link.label}
                {...serviceLink(alert.service, routeHref(link.route))}
                className="rounded-md border border-line bg-panel px-2 py-1 text-xs text-link no-underline transition-colors duration-150 hover:bg-band"
              >
                {link.label}
              </a>
            ))}
          </div>
        </article>
      ))}
    </div>
  );
}
